import { useState, useEffect } from "react";
import { useAuth, api } from "../App";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  BarChart3, Loader2, CheckCircle2, Vote, ChevronRight,
  Clock, Users, ArrowLeft, Lock
} from "lucide-react";

const PollPage = () => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [votingPollId, setVotingPollId] = useState(null);
  const [selectedPoll, setSelectedPoll] = useState(null);
  useAuth(); // Ensure user is authenticated
  const _navigate = useNavigate();

  useEffect(() => {
    fetchPolls();
  }, []);

  const fetchPolls = async () => {
    try {
      const response = await api.get("/polls");
      setPolls(response.data);
    } catch (_error) {
      toast.error("Failed to load polls");
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (pollId, optionIndex) => {
    setVotingPollId(pollId);
    try {
      const response = await api.post(`/polls/${pollId}/vote`, { option_index: optionIndex });
      toast.success("Vote cast successfully!");
      
      // Update poll in state with new results
      setPolls(prev => prev.map(poll => {
        if (poll.id === pollId) {
          return {
            ...poll,
            user_voted: true,
            user_vote_option: optionIndex,
            option_votes: response.data.option_votes,
            total_votes: response.data.total_votes
          };
        }
        return poll;
      }));
      
      // Also update selected poll if viewing details
      if (selectedPoll?.id === pollId) {
        setSelectedPoll(prev => ({
          ...prev,
          user_voted: true,
          user_vote_option: optionIndex,
          option_votes: response.data.option_votes,
          total_votes: response.data.total_votes
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cast vote");
    } finally {
      setVotingPollId(null);
    }
  };

  const getVotePercentage = (votes, total) => {
    if (!total || total === 0) return 0;
    return Math.round((votes / total) * 100);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Poll Detail View
  if (selectedPoll) {
    // Check if results should be shown (admin enabled show_results)
    const canShowResults = selectedPoll.show_results && selectedPoll.option_votes !== null;
    
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-6 px-4">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedPoll(null)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Polls
          </Button>

          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-xl">{selectedPoll.question}</CardTitle>
                  <CardDescription className="flex items-center gap-2 mt-2">
                    <Clock className="w-4 h-4" />
                    Posted on {formatDate(selectedPoll.created_at)}
                    {/* Only show total votes if show_results is enabled */}
                    {canShowResults && selectedPoll.total_votes !== null && (
                      <span className="flex items-center gap-1 ml-4">
                        <Users className="w-4 h-4" />
                        {selectedPoll.total_votes} votes
                      </span>
                    )}
                  </CardDescription>
                </div>
                {selectedPoll.user_voted && (
                  <Badge className="bg-green-100 text-green-700">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Voted
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedPoll.options.map((option, index) => {
                const isSelected = selectedPoll.user_vote_option === index;
                const voteCount = canShowResults ? selectedPoll.option_votes[index] : 0;
                const percentage = canShowResults ? getVotePercentage(voteCount, selectedPoll.total_votes) : 0;

                return (
                  <div key={`poll-${option}-${index}`} className="relative">
                    {/* Vote Button (if not voted) */}
                    {!selectedPoll.user_voted ? (
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left h-auto py-4 px-4"
                        onClick={() => handleVote(selectedPoll.id, index)}
                        disabled={votingPollId === selectedPoll.id}
                      >
                        <div className="flex items-center gap-3 w-full">
                          <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center">
                            {index + 1}
                          </div>
                          <span className="flex-1">{option}</span>
                          {votingPollId === selectedPoll.id && (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          )}
                        </div>
                      </Button>
                    ) : (
                      /* After voting view */
                      <div 
                        className={`relative overflow-hidden rounded-lg border p-4 ${
                          isSelected ? 'border-primary bg-primary/5' : 'border-muted'
                        }`}
                      >
                        {/* Progress bar - only show if results are enabled */}
                        {canShowResults && (
                          <div 
                            className={`absolute inset-0 ${isSelected ? 'bg-primary/20' : 'bg-muted/50'}`}
                            style={{ width: `${percentage}%` }}
                          />
                        )}
                        <div className="relative flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {isSelected && (
                              <CheckCircle2 className="w-5 h-5 text-primary" />
                            )}
                            <span className={isSelected ? 'font-medium' : ''}>{option}</span>
                          </div>
                          {/* Only show vote count and percentage if admin enabled show_results */}
                          {canShowResults && (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">{voteCount} votes</span>
                              <Badge variant="secondary">{percentage}%</Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Message when voted but results are hidden */}
              {selectedPoll.user_voted && !selectedPoll.show_results && (
                <div className="flex items-center justify-center gap-2 p-4 bg-muted/50 rounded-lg mt-4">
                  <Lock className="w-4 h-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Results will be visible once the poll ends
                  </p>
                </div>
              )}

              {!selectedPoll.user_voted && (
                <p className="text-sm text-muted-foreground text-center mt-4">
                  Cast your vote to participate
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Polls List View
  return (
    <Layout>
      <div className="max-w-2xl mx-auto py-6 px-4" data-testid="poll-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-primary/10">
            <Vote className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Polls</h1>
            <p className="text-sm text-muted-foreground">Share your opinion</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : polls.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-12 text-center">
              <BarChart3 className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
              <p className="text-lg font-medium mb-2">No Polls Available</p>
              <p className="text-sm text-muted-foreground">
                Check back later for new polls
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {polls.map((poll) => {
              // Only show vote counts in list if show_results is true
              const showVoteCount = poll.show_results && poll.total_votes !== null;
              
              return (
                <Card 
                  key={poll.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedPoll(poll)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg mb-2">{poll.question}</h3>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDate(poll.created_at)}
                          </span>
                          <span>{poll.options.length} options</span>
                          {/* Only show vote count if admin enabled show_results */}
                          {showVoteCount && (
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {poll.total_votes} votes
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {poll.user_voted && (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Voted
                          </Badge>
                        )}
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>

                    {/* Quick Preview of Options */}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {poll.options.slice(0, 3).map((option, index) => (
                        <Badge key={`badge-${option}-${index}`} variant="outline" className="text-xs">
                          {option.length > 20 ? option.substring(0, 20) + '...' : option}
                        </Badge>
                      ))}
                      {poll.options.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{poll.options.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PollPage;
